import { useEffect, useMemo, useState } from 'react';

import { plannerDevToolsState } from '@trello/business-logic/planner';
import { usePlannerAccountId } from '@trello/id-context';

import { usePlannerAccountByIdFragment } from './PlannerAccountByIdFragment.generated';
import { usePlannerAccounts } from './usePlannerAccounts';

/**
 * Custom hook to retrieve planner account data for a specific account.
 *
 * Usage patterns:
 * 1. Explicit account: Pass accountId parameter (usePlannerAccount({ accountId: '...' }))
 * 2. Context-based: Wrap component in PlannerAccountIdProvider and call with no args
 * 3. For primary account: Use primaryAccount?.node from usePlannerAccounts() instead
 *
 * The hook will:
 * 1. Use the provided accountId if specified
 * 2. Fall back to accountId from context (via PlannerAccountIdProvider) if available
 * 3. Return undefined if neither is provided
 *
 * Returns the account data for the specified account ID.
 *
 * @param options - Configuration options
 * @param options.accountId - Optional account ID to fetch specific account data
 * @returns {Object} Account data including:
 * - accountType: The type of account (e.g. 'GOOGLE', 'OUTLOOK')
 * - enabledCalendars: List of calendars enabled for this account
 * - enabledCalendarsPageInfo: Pagination info for enabled calendars
 * - isLoadingEnabledCalendars: Whether enabled calendars are still loading
 * - outboundAuthId: ID for outbound authentication
 * - plannerAccountDisplayName: Display name for the account
 * - plannerAccountId: ID of the planner account
 * - providerCalendar: Calendar provider details
 * - primaryCalendarId: ID of the primary calendar
 */
export const usePlannerAccount = ({
  accountId,
}: {
  accountId?: string;
} = {}) => {
  const [isLoadingEnabledCalendars, setIsLoadingEnabledCalendars] =
    useState(true);

  const accountIdFromContext = usePlannerAccountId();
  const { primaryAccountId } = usePlannerAccounts();
  const providedAccountId = accountId || accountIdFromContext;

  const { data } = usePlannerAccountByIdFragment({
    from: { id: providedAccountId },
    optimistic: true,
  });

  const account = data || undefined;

  const enabledCalendars = useMemo(() => {
    return (
      account?.enabledCalendars?.edges
        ?.map((edge) => edge.node)
        .filter(Boolean) || undefined
    );
  }, [account]);

  const providerCalendar = useMemo(() => {
    return account?.providerCalendars || undefined;
  }, [account]);

  // This id will only return a value if the primary calendar is enabled
  const primaryCalendarId = useMemo(() => {
    return enabledCalendars?.find((calendar) => calendar?.isPrimary)?.id;
  }, [enabledCalendars]);

  plannerDevToolsState.setValue({
    ...plannerDevToolsState,
    accountId: account?.id || '',
  });

  const isExpired = useMemo(
    () => (account?.isExpired === undefined ? undefined : account?.isExpired),
    [account],
  );

  const isPrimary = useMemo(() => {
    if (!account?.id || !primaryAccountId) {
      return undefined;
    }
    return account.id === primaryAccountId;
  }, [account, primaryAccountId]);

  const isProblematic = useMemo(() => {
    if (!account) {
      return undefined;
    }
    return isExpired || !account?.hasRequiredScopes;
  }, [account, isExpired]);

  useEffect(() => {
    if (account?.enabledCalendars?.pageInfo?.hasNextPage === false) {
      setIsLoadingEnabledCalendars(false);
    }
  }, [account]);

  return {
    account,
    accountType: account?.accountType || undefined,
    enabledCalendars,
    enabledCalendarsPageInfo: account?.enabledCalendars?.pageInfo || undefined,
    isLoadingEnabledCalendars: account ? isLoadingEnabledCalendars : undefined,
    outboundAuthId: account?.outboundAuthId || undefined,
    plannerAccountDisplayName: account?.displayName || undefined,
    plannerAccountId: account?.id || undefined,
    providerCalendar,
    primaryCalendarId,
    isExpired,
    hasRequiredScopes: account?.hasRequiredScopes,
    isPrimary,
    isProblematic,
  };
};
